import intersection from 'lodash/intersection';
import type { Moment } from 'moment/moment';

import { EARLY_SURCHARGE_END_TIME, LATE_SURCHARGE_START_TIME } from '../constants/appointment';
import { SURCHARGES_CONFIG_BY_COUNTRY } from '../constants/country';
import type {
  BasketContentItem,
  BasketContentItemWithPrices,
  ComboPrice,
  GiftCard,
  Prestation,
  PrestationOrCategoryPrice,
  PrestationReference,
  PricesByZone,
  Promotion,
  Universe,
  ZoneId,
} from '../types/commons';
import { getCurrentCountry } from '../utils/country';
import moment, { fromDateAndTime } from '../utils/moment';
import { getAllPrestationsForCategory } from '../utils/prestations';
import { parseTimeDeprecated } from '../utils/time';

const getComboPriceForPrestation = (comboPrices: ComboPrice[], prestationRef: PrestationReference) =>
  comboPrices.find((comboPrice) => comboPrice.prestations.includes(prestationRef));

type CalculateReducedPriceParams = {
  basePrice: number;
  universeCategoryPrestations: Prestation[];
  prestationReference: PrestationReference;
  basketCategoryPrestations: PrestationReference[];
  comboPrices: ComboPrice[];
  zone: ZoneId | null;
};

const calculateReducedPrice = ({
  basePrice,
  universeCategoryPrestations,
  prestationReference,
  basketCategoryPrestations,
  comboPrices,
  zone,
}: CalculateReducedPriceParams) => {
  const comboPrice = getComboPriceForPrestation(comboPrices, prestationReference);

  if (!comboPrice) return basePrice;

  const complexPrestations = universeCategoryPrestations
    .filter((prestation) => prestation.complex)
    .map((prestation) => prestation.reference);
  const combo = [...comboPrice.combo_with, ...(comboPrice.combo_include_complex_prestations ? complexPrestations : [])];

  if (intersection(combo, basketCategoryPrestations).length === 0) return basePrice;

  if (comboPrice.price_by_zone && zone) {
    return comboPrice.price_by_zone[zone] || basePrice;
  }

  if (comboPrice.price_adjustment) {
    return basePrice + comboPrice.price_adjustment;
  }

  return basePrice;
};

export const getPriceByZoneOrPriceItself = (prices: PricesByZone, zone: ZoneId | null) => {
  if (Array.isArray(prices)) {
    const priceForZone = prices.find((p) => p[0] === zone);
    return priceForZone ? priceForZone[1] : 0;
  }

  return prices;
};

export const getPrestationPriceFromUniverseCategory = (
  universeCategoryPrestations: Prestation[],
  prestationReference: PrestationReference,
  basketCategoryPrestations: PrestationReference[],
  comboPrices: ComboPrice[],
  zone: ZoneId | null,
  membershipComboPrices: ComboPrice[],
): PrestationOrCategoryPrice => {
  const universePrestation = universeCategoryPrestations.find(
    (prestation) => prestation.reference === prestationReference,
  );

  if (!universePrestation) {
    return {
      standard: 0,
      reduced: 0,
    };
  }

  const standardPrice = getPriceByZoneOrPriceItself(universePrestation.prices, zone);
  const reducedStandardPrice = calculateReducedPrice({
    basePrice: standardPrice,
    universeCategoryPrestations,
    prestationReference,
    basketCategoryPrestations,
    comboPrices,
    zone,
  });

  const membershipPrice = getPriceByZoneOrPriceItself(universePrestation.member_prices, zone);
  const reducedMembershipPrice = calculateReducedPrice({
    basePrice: membershipPrice,
    universeCategoryPrestations,
    prestationReference,
    basketCategoryPrestations,
    comboPrices: membershipComboPrices,
    zone,
  });

  return {
    standard: standardPrice,
    reduced: reducedStandardPrice,
    membership: membershipPrice,
    membershipReduced: reducedMembershipPrice,
  };
};

const getBasketPrestationsPrices = (
  basketCategory: BasketContentItem,
  universe: Universe,
  zone: ZoneId | null,
  comboPrices: ComboPrice[],
  membershipComboPrices: ComboPrice[],
): Record<PrestationReference, PrestationOrCategoryPrice> => {
  const universeCategoryPrestations = getAllPrestationsForCategory(basketCategory.reference, universe);

  return basketCategory.prestations.reduce(
    (acc, reference) => ({
      ...acc,
      [reference]: getPrestationPriceFromUniverseCategory(
        universeCategoryPrestations,
        reference,
        basketCategory.prestations,
        comboPrices,
        zone,
        membershipComboPrices,
      ),
    }),
    {},
  );
};

export const basketContentPrices = (
  basketCategory: BasketContentItem,
  universe: Universe,
  zone: ZoneId | null,
  comboPrices: ComboPrice[],
  membershipComboPrices: ComboPrice[],
): BasketContentItemWithPrices => {
  const prestationsPrices = getBasketPrestationsPrices(
    basketCategory,
    universe,
    zone,
    comboPrices,
    membershipComboPrices,
  );
  return {
    ...basketCategory,
    price: Object.values(prestationsPrices).reduce(
      (sum, price) => ({
        standard: sum.standard + price.standard,
        reduced: sum.reduced + price.reduced,
        membership: (sum.membership || 0) + (price.membership || 0),
        membershipReduced: (sum.membershipReduced || 0) + (price.membershipReduced || 0),
      }),
      { standard: 0, reduced: 0, membership: 0, membershipReduced: 0 },
    ),
    prestationsPrices,
  };
};

type CalculatePromoAndGiftValuesParams = {
  priceBeforeReduction: number;
  promotion?: Promotion | null;
  giftCard?: GiftCard;
  durationInHours?: number;
};

export const calculatePromoAndGiftValues = ({
  priceBeforeReduction,
  promotion,
  giftCard,
  durationInHours = 0,
}: CalculatePromoAndGiftValuesParams) => {
  // Promo code
  let promoValue = 0;
  if (promotion) {
    const { amount, calculation_type: calculationType } = promotion;
    switch (calculationType) {
      case 'per_hour':
        promoValue = amount * durationInHours;
        break;
      case 'percent':
        promoValue = Math.floor((amount * priceBeforeReduction) / 100);
        break;
      case 'fixed':
      default:
        promoValue = amount;
        break;
    }
  }

  // Gift card
  const giftValue = giftCard?.value ? giftCard.value : 0;

  return { promoValue, giftValue };
};

export const getHourlyAppointmentSurchargeReasonDeprecated = (appointment?: Moment | null) => {
  const areEarlyAndLateHourSurchargesEnabled =
    SURCHARGES_CONFIG_BY_COUNTRY[getCurrentCountry()].hasEarlyAndLateHourSurcharges;

  if (!appointment || !areEarlyAndLateHourSurchargesEnabled) return undefined;

  const earlyAppointment = fromDateAndTime(appointment, parseTimeDeprecated(EARLY_SURCHARGE_END_TIME));
  const lateAppointment = fromDateAndTime(appointment, parseTimeDeprecated(LATE_SURCHARGE_START_TIME));

  if (appointment.isBefore(earlyAppointment)) return 'before_8am';
  if (appointment.isSameOrAfter(lateAppointment)) return 'after_8pm';

  return undefined;
};

export const getDailyAppointmentSurchargeReasonDeprecated = (date?: Moment | null, holidays?: string[]) => {
  const areSundaySurchargesEnabled = SURCHARGES_CONFIG_BY_COUNTRY[getCurrentCountry()].hasSundaySurcharges;

  if (!date) return undefined;
  if (date.day() === 0 && areSundaySurchargesEnabled) return 'sunday';
  if (holidays?.some((holiday) => moment(holiday).isSame(date, 'day'))) return 'holiday';

  return undefined;
};

export const getAppointmentSurchargeReasonDeprecated = (appointment?: Moment | null, holidays?: string[]) =>
  getHourlyAppointmentSurchargeReasonDeprecated(appointment) ||
  getDailyAppointmentSurchargeReasonDeprecated(appointment, holidays);

export const isAppointmentDailySurchargedDeprecated = (date: Moment | null, holidays: string[]) =>
  !!getDailyAppointmentSurchargeReasonDeprecated(date, holidays);
